import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import { useTranslation } from "react-i18next";
import {
  Table,
  Pagination,
  Row,
  Col,
  Form,
  DropdownButton,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import { useTable, usePagination } from "react-table";
import { DropdownSubmenu } from "react-bootstrap-submenu";
import NotyfContext from "../../../../contexts/NotyfContext";
import FileSaver from "file-saver";
import { exportTimeoutReport } from "../../../../redux/slices/reports";

const TimeoutReportTable = ({
  currentPageNumber,
  pageSize,
  setPage,
  setPageSize,
  timeoutReportId,
}) => {
  const timeoutReportLines = useSelector(
    (state) => state.reports.timeoutReportLines
  );
  const timeoutReportColumns = useSelector(
    (state) => state.reports.timeoutReportColumns
  );
  const { t } = useTranslation();
  const { backgroundColor, fontColor } = useWhitelabel();
  const notyf = useContext(NotyfContext);

  const exportToCSV = async () => {
    const format = "csv";
    notyf.open({
      type: "success",
      message: "Timeout Report will be exported shortly",
      duration: 5000,
      ripple: true,
      dismissible: false,
      position: {
        x: "center",
        y: "top",
      },
    });
    await exportTimeoutReport({ id: timeoutReportId, format: format })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "text/csv",
        });
        FileSaver.saveAs(blob, "report.csv");
      })
      .catch((error) => {
        notyf.open({
          type: "danger",
          message: error,
          ripple: true,
          dismissible: false,
          duration: 5000,
          position: {
            x: "center",
            y: "top",
          },
        });
      });
  };

  const exportToPDF = async () => {
    const format = "pdf";
    notyf.open({
      type: "success",
      message: "Timeout Report will be exported shortly",
      duration: 5000,
      ripple: true,
      dismissible: false,
      position: {
        x: "center",
        y: "top",
      },
    });
    await exportTimeoutReport({ id: timeoutReportId, format: format })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/pdf",
        });
        FileSaver.saveAs(blob, "report.pdf");
      })
      .catch((error) => {
        notyf.open({
          type: "danger",
          message: error,
          ripple: true,
          dismissible: false,
          duration: 5000,
          position: {
            x: "center",
            y: "top",
          },
        });
      });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page: rows,
  } = useTable(
    {
      columns: timeoutReportColumns,
      data: timeoutReportLines.lines || [],
      manualPagination: true,
      pageCount: timeoutReportLines.pages || 0,
      initialState: { pageIndex: currentPageNumber - 1, pageSize },
    },
    usePagination
  );

  const renderPaginationControls = () => (
    <Row className="mt-4">
      <Col md="9">
        <span className="mx-2">
          {t("table.page")}{" "}
          <strong>
            {currentPageNumber} {t("table.of")} {timeoutReportLines.pages}
          </strong>
        </span>
        <span className="ms-3 me-2">{t("table.show")}:</span>
        <Form.Select
          className="d-inline-block w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[5, 10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Form.Select>

        <span className="ms-3 me-2">{t("table.goToPage")}:</span>
        <Form.Control
          className="d-inline-block"
          type="number"
          value={currentPageNumber}
          min={1}
          max={timeoutReportLines.pages || 1}
          onChange={(e) => {
            const newPage = e.target.value ? Number(e.target.value) : 1;
            setPage(newPage);
          }}
          style={{ width: "75px" }}
        />
      </Col>
      <Col md="3">
        <Pagination className="float-end">
          <Pagination.First
            onClick={() => setPage(1)}
            disabled={currentPageNumber === 1}
          />
          <Pagination.Prev
            onClick={() => setPage(currentPageNumber - 1)}
            disabled={currentPageNumber === 1}
          />
          <Pagination.Next
            onClick={() => setPage(currentPageNumber + 1)}
            disabled={currentPageNumber >= timeoutReportLines.pages}
          />
          <Pagination.Last
            onClick={() => setPage(timeoutReportLines.pages)}
            disabled={currentPageNumber >= timeoutReportLines.pages}
          />
        </Pagination>
      </Col>
    </Row>
  );

  return (
    <>
      <Row>
        <Col className="ms-auto" md="auto">
          <DropdownButton
            as={ButtonGroup}
            style={{
              "--dynamic-bg-color": backgroundColor,
              "--dynamic-font-color": fontColor,
              border: "none",
            }}
            title={t("reports.actions")}
          >
            <DropdownSubmenu title={t("reports.export")}>
              <Dropdown.Item eventKey="CSV" onClick={() => exportToCSV()}>
                CSV
              </Dropdown.Item>
              <Dropdown.Item eventKey="PDF" onClick={() => exportToPDF()}>
                PDF
              </Dropdown.Item>
            </DropdownSubmenu>
          </DropdownButton>
        </Col>
      </Row>
      <Table {...getTableProps()} striped hover responsive>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>

      {timeoutReportLines.lines?.length > 0 && renderPaginationControls()}
    </>
  );
};

export default TimeoutReportTable;
